
import { useMemo } from 'react';
import { Star } from "lucide-react";

interface DealerRatingProps {
  rating: number;
  rating_count?: number; // Add rating_count prop
  showValue?: boolean;
  size?: "sm" | "md" | "lg";
}

export function DealerRating({ 
  rating, 
  rating_count = 0, // Add rating_count with default value
  showValue = true, 
  size = "md" 
}: DealerRatingProps) {
  // Ensure we have a valid number rating
  const validRating = useMemo(() => {
    // First convert to a number if needed
    let numRating = 0;
    
    if (typeof rating === 'number' && !isNaN(rating)) {
      numRating = rating;
    } else if (typeof rating === 'string') {
      numRating = parseFloat(rating) || 0;
    }
    
    // Limit between 0 and 5
    return Math.min(5, Math.max(0, numRating));
  }, [rating]);

  // Log the rating for debugging - with both raw and processed values
  console.log(`DealerRating component - Raw rating: ${rating} (${typeof rating}), processed: ${validRating}, rating_count: ${rating_count}`);

  // Size classes
  const sizeClasses = {
    sm: "h-3 w-3",
    md: "h-4 w-4",
    lg: "h-5 w-5",
  };

  // Render stars
  const renderStars = () => {
    const stars = [];
    
    for (let i = 1; i <= 5; i++) {
      const isFilled = i <= Math.floor(validRating);
      const isHalfFilled = !isFilled && i === Math.ceil(validRating) && validRating % 1 !== 0;
      
      stars.push(
        <Star
          key={i}
          className={`${sizeClasses[size]} ${
            isFilled || isHalfFilled ? "text-yellow-500" : "text-gray-300"
          } ${isFilled ? "fill-yellow-500" : isHalfFilled ? "fill-yellow-500/50" : "fill-none"}`}
        />
      );
    }
    
    return stars;
  };
  
  return (
    <div className="flex items-center">
      <div className="flex">{renderStars()}</div>
      {showValue && validRating > 0 && (
        <span className="ml-1 text-sm text-muted-foreground">
          {validRating.toFixed(1)}
        </span>
      )}
      {rating_count > 0 && (
        <span className="ml-2 text-xs text-muted-foreground">
          ({rating_count} recensioni)
        </span>
      )}
    </div>
  );
}
